import { attr, belongsTo } from '@ember-data/model';
import { computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { alias, gt } from '@ember/object/computed';
import { isNone } from '@ember/utils';
import CatalogObject, { CatalogObjectType } from '@square/dashboard-items-data/models/connect/catalog-object';
import Enum from '@square/dashboard-utilities/utils/enum';
import Money from '@square/dashboard-money/models/money';
import { CatalogObjectType as CatalogObjectTypeProto } from '@square/dashboard-protos/models/squareup/connect/v2/catalog/resources/enums';
export const LinkType = new Enum(['PAYMENT_LINK', 'DONATION_LINK', 'ITEM_LINK', 'CLASS_LINK', 'SERVICE_LINK']);

// There are no event link types, they are item links with events
export const LinkTypeAliasEventLink = 'EVENT_LINK';

/**
 * Some links (like Classes & Events) are built off of other link types (like item links).
 * This is only used on frontend to control the experience, since the underlying
 * link type (item) may be different than the intended experience (event).
 */
export const LinkSubType = new Enum(['ITEM_EVENT_LINK']);
export default CatalogObject.extend({
  intl: service(),
  type: attr('string', {
    defaultValue: CatalogObjectType.CHECKOUT_LINK,
    protoEnum: CatalogObjectTypeProto
  }),
  checkoutLinkData: belongsTo('connect/checkout-link/data', {
    async: false
  }),
  // Alias' for checkoutLinkData
  name: alias('checkoutLinkData.name'),
  enabled: alias('checkoutLinkData.enabled'),
  linkType: alias('checkoutLinkData.linkType'),
  orderDetails: alias('checkoutLinkData.orderDetails'),
  donationGoal: alias('checkoutLinkData.donationGoal'),
  subscriptionPlans: alias('checkoutLinkData.subscriptionPlans'),
  allowTipping: alias('checkoutLinkData.allowTipping'),
  subscriptionOnly: alias('checkoutLinkData.subscriptionOnly'),
  redirectUrl: alias('checkoutLinkData.redirectUrl'),
  customFields: alias('checkoutLinkData.customFields'),
  source: alias('checkoutLinkData.source'),
  url: alias('checkoutLinkData.url'),
  imageIds: alias('checkoutLinkData.imageIds'),
  primaryImage: computed('imageIds.{length,firstObject}', 'store', function () {
    if (this.imageIds?.length > 0) {
      return this.store.peekRecord('connect/image', this.imageIds.firstObject);
    }
    return null;
  }),
  /**
   *  Used for link reporting. Not saved to DB.
   *  @var {Number} totalSalesAmount
   */
  totalSalesAmount: 0,
  firstLineItem: alias('orderDetails.lineItems.firstObject'),
  hasSubscriptions: gt('subscriptionPlans.length', 0),
  firstSubscriptionPlan: alias('subscriptionPlans.firstObject'),
  /*
   * We use lastObject because free trials plan have 2 cadences
   * with the first being the free trial period.
   */
  firstSubscriptionCadence: alias('firstSubscriptionPlan.phases.lastObject.cadence'),
  subscriptionIds: computed('subscriptionPlans', function () {
    return this.subscriptionPlans.map(subscriptionPlan => subscriptionPlan.id);
  }),
  linkImage: computed('linkType', 'primaryImage', 'firstLineItem.item.primaryImage', function () {
    if (this.linkType === LinkType.ITEM_LINK) {
      return this.firstLineItem.item?.primaryImage;
    }
    return this.primaryImage;
  }),
  checkoutButton: computed('linkType', 'firstLineItem.item.ecomBuyButtonText', 'checkoutLinkData.checkoutButton.text', {
    get() {
      if (this.linkType === LinkType.ITEM_LINK) {
        return {
          text: this.firstLineItem.item?.ecomBuyButtonText
        };
      }
      return this.checkoutLinkData?.checkoutButton;
    },
    set(key, value) {
      if (this.linkType === LinkType.ITEM_LINK) {
        this.set('firstLineItem.item.ecomBuyButtonText', value);
      } else {
        this.set('checkoutLinkData.checkoutButton', value);
      }
      return value;
    }
  }),
  isBuyerConfigurable: computed('linkType', 'firstLineItem.priceMoney', function () {
    if ([LinkType.PAYMENT_LINK, LinkType.DONATION_LINK, LinkType.CLASS_LINK, LinkType.SERVICE_LINK].includes(this.linkType)) {
      return !this.firstLineItem.priceMoney;
    }
    return false;
  }),
  firstLineItemEvent: computed('isIsolatedItemLink', 'firstLineItem.item.event', function () {
    if (this.isIsolatedItemLink) {
      return null;
    }
    return this.firstLineItem?.item?.event;
  }),
  /**
   * Since event links are actually item links, we also need to check
   * that the item has an event for it to be an event link.
   */
  isEventLink: computed('linkType', 'firstLineItemEvent', function () {
    return this.linkType === LinkType.ITEM_LINK && this.firstLineItemEvent;
  }),
  description: computed('checkoutLinkData.description', 'firstLineItem.item.description', 'linkType', {
    get() {
      return this.linkType === LinkType.ITEM_LINK ? this.firstLineItem.item?.description : this.checkoutLinkData?.description;
    },
    set(key, value) {
      if (this.linkType === LinkType.ITEM_LINK) {
        this.set('firstLineItem.item.description', value);
      } else {
        this.set('checkoutLinkData.description', value);
      }
      return value;
    }
  }),
  displayAmount: computed('firstLineItem.{item.displayPriceRange,priceMoney.formatted}', 'isIsolatedItemLink', 'linkType', function () {
    if ([LinkType.PAYMENT_LINK, LinkType.DONATION_LINK, LinkType.CLASS_LINK, LinkType.SERVICE_LINK].includes(this.linkType)) {
      if (this.firstLineItem.priceMoney) {
        return this.firstLineItem.priceMoney.formatted;
      }
      // Buyer entered amount
      return this.intl.t('ecom.onlineCheckout.checkoutLink.donation.displayAmount');
    }
    if (this.linkType === LinkType.ITEM_LINK && !this.isIsolatedItemLink) {
      return this.firstLineItem.item?.displayPriceRange;
    }
    return '';
  }),
  displayLinkType: computed('linkType', 'isEventLink', function () {
    if (this.linkType === LinkType.PAYMENT_LINK) {
      return this.intl.t('ecom.onlineCheckout.blade.type.payment');
    }
    if (this.linkType === LinkType.DONATION_LINK) {
      return this.intl.t('ecom.onlineCheckout.blade.type.donation');
    }
    if (this.linkType === LinkType.ITEM_LINK) {
      if (this.isEventLink) {
        return this.intl.t('ecom.onlineCheckout.blade.type.event');
      } else {
        return this.intl.t('ecom.onlineCheckout.blade.type.item');
      }
    }
    if (this.linkType === LinkType.CLASS_LINK) {
      return this.intl.t('ecom.onlineCheckout.blade.type.class');
    }
    if (this.linkType === LinkType.SERVICE_LINK) {
      return this.intl.t('ecom.onlineCheckout.blade.type.service');
    }
    return '';
  }),
  displayTotalSales: computed('currency', 'totalSalesAmount', function () {
    if (this.totalSalesAmount <= 0) {
      return '-';
    }

    // This will default to the merchant currency.
    return Money.fromCents(this.totalSalesAmount).formatted;
  }),
  isIsolatedItemLink: computed('firstLineItem.{item,itemId}', 'linkType', 'store', function () {
    if (this.linkType !== LinkType.ITEM_LINK) {
      return false;
    }
    if (isNone(this.firstLineItem) || isNone(this.firstLineItem.itemId)) {
      return true;
    }

    /**
     * NOTE (alexbbt): This checks if the related item is loaded. If the item is not loaded,
     * it usually means that it was deleted. Since accessing `this.firstLineItem.item` when
     * the item is not loaded would be an error, this is a safe way to check.
     */
    const item = this.store.peekRecord('connect/item', this.firstLineItem.itemId);
    if (isNone(item)) {
      return true;
    }
    return false;
  }),
  /**
   *  Free trial subscription plans have 2 phases
   *  with the first phase being the free trial
   *  phase with a price of zero
   */
  hasFreeTrialSubscription: computed('firstSubscriptionPlan.phases.{firstObject.recurringPriceMoney.amount,length}', 'hasSubscriptions', function () {
    return this.hasSubscriptions && this.firstSubscriptionPlan?.phases?.length > 1 && this.firstSubscriptionPlan?.phases?.firstObject?.recurringPriceMoney?.amount === 0;
  })
});